/**
 * Provisions Service - Modernized for Angular 20+
 * Follows the same patterns as PeopleService for consistency.
 *
 * API Endpoints:
 * - POST api/{org}/provisions - Paginated list, returns { provisions: [...], lastEvaluatedKey, count }
 * - GET api/{org}/provisions/contract-id/{id} - By contractId, returns { Items: [...], Count }
 * - GET api/{org}/provisions/contract-num/{id} - By contractNum, returns { Items: [...], Count }
 * - PUT api/{org}/provisions/write - Save provision with { provNum, provData, contractId?, contractNum? }
 *
 * Data structure: Provisions use a { provNum, provData } wrapper structure
 * where provData contains the actual Provision object.
 */
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  of,
  throwError,
  map,
  tap,
  catchError,
  take,
  filter,
  switchMap
} from 'rxjs';
import { DatabaseAction, EntityType } from '../../objects/interface-library';
import { PaginationService } from '../pagination.service';
import { Provision } from '../../objects/provision';
import { CLAUSE_LABELS_BY_KEYSECTION } from '../../objects/clause-sections';
import { environment } from '../../../environments/environment';
import { ExtractedKeySections, KeySection } from '../ai/document-analysis.service';

@Injectable({
  providedIn: 'root'
})
export class ProvisionsService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  // BehaviorSubject to hold all provisions (extracted from provData wrapper)
  private allProvisionsSubject = new BehaviorSubject<Provision[]>([]);
  public allProvisions$ = this.allProvisionsSubject.asObservable();

  // Track if cache has been refreshed at least once
  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();

  constructor() {
    this.refreshAllProvisionsRecords().subscribe({
      next: () => {
        this.isCacheRefreshedSubject.next(true);
      },
      error: (err) => {
        console.error('Initial provisions cache refresh failed:', err);
        this.isCacheRefreshedSubject.next(false);
      }
    });
  }

  /**
   * Private function to fetch a single page of provisions from the API.
   * POST api/{org}/provisions with body { lastEvaluatedKey?, limit? }
   * Response: { provisions: [...], lastEvaluatedKey: object|null, count: n }
   * @param lastEvaluatedKey The key for the next page of results.
   */
  private getPageOfProvisions(lastEvaluatedKey: any = null): Observable<any> {
    const body: { lastEvaluatedKey?: any; limit?: number } = {};
    if (lastEvaluatedKey) {
      body.lastEvaluatedKey = lastEvaluatedKey;
    }

    const options = {
      headers: { 'Content-Type': 'application/json' }
    };

    return this.http.post<{ provisions: any[]; lastEvaluatedKey: any; count: number }>(
      environment.provisionsByPaginationUrl, body, options
    ).pipe(
      map(response => ({
        items: response.provisions || [],
        lastEvaluatedKey: response.lastEvaluatedKey
      })),
      catchError(error => {
        console.error('Error fetching provisions page:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refreshes all provisions by calling the paginated API and updating the subject.
   * Returns an Observable so callers can wait for completion.
   */
  public refreshAllProvisionsRecords(): Observable<Provision[]> {
    return this.paginationService.getAllPaginatedDataProgressively(key => this.getPageOfProvisions(key))
      .pipe(
        map(allProvisionsRaw => {
          const filtered = allProvisionsRaw.filter((item: any) => item != null);

          const extracted = filtered.map((item: any) => {
            if (item.provData) {
              return {
                ...item.provData,
                provNum: item.provNum || item.provData.provNum
              };
            }
            return item;
          });

          return extracted.filter((provision: any) => provision && provision.provNum);
        }),
        tap(provisionsList => this.allProvisionsSubject.next(provisionsList)),
        catchError(error => {
          console.error('Error fetching all provisions:', error);
          this.allProvisionsSubject.next([]);
          return throwError(() => error);
        })
      );
  }

  /**
   * Gets provisions by contract number from the API.
   * GET api/{org}/provisions/contract-num/{contractNum}
   * Response: { Items: [...], Count: n }
   * @param contractNum The contract number to filter by.
   * @returns An observable that emits the filtered provisions.
   */
  public getProvisionsByContractNum(contractNum: string): Observable<Provision[]> {
    return this.http.get<{ Items: any[]; Count: number }>(
      `${environment.provisionContractNumUrl}${contractNum}`
    ).pipe(
      map(response => {
        // Extract provData from wrapper structure
        return (response.Items || [])
          .filter((item: any) => item && item.provData)
          .map((item: any) => ({
            ...item.provData,
            provNum: item.provNum || item.provData.provNum
          }));
      }),
      // tap(provisions => console.log(`Found ${provisions.length} provisions for contractNum: ${contractNum}`)),
      catchError(error => {
        console.error('Error fetching provisions by contract number:', error);
        return of([]);
      })
    );
  }

  /**
   * Gets provisions by contract ID from the API.
   * GET api/{org}/provisions/contract-id/{contractId}
   * Response: { Items: [...], Count: n }
   * @param contractId The contract ID to filter by.
   * @returns An observable that emits the filtered provisions.
   */
  public getProvisionsByContractId(contractId: string): Observable<Provision[]> {
    return this.http.get<{ Items: any[]; Count: number }>(
      `${environment.provisionContractIdUrl}${contractId}`
    ).pipe(
      map(response => {
        // Extract provData from wrapper structure
        return (response.Items || [])
          .filter((item: any) => item && item.provData)
          .map((item: any) => ({
            ...item.provData,
            provNum: item.provNum || item.provData.provNum
          }));
      }),
      // tap(provisions => console.log(`Found ${provisions.length} provisions for contractId: ${contractId}`)),
      catchError(error => {
        console.error('Error fetching provisions by contract ID:', error);
        return of([]);
      })
    );
  }

  /**
   * Gets provisions filtered by source ID from the cache.
   * Use getProvisionsByContractId() for direct API calls when sourceType is Contract.
   * @param sourceId The source entity ID.
   * @returns An observable that emits the filtered provisions.
   */
  public getProvisionsBySourceId(sourceId: string): Observable<Provision[]> {
    return this.allProvisions$.pipe(
      map(provisions => {
        return provisions.filter(provision => provision.sourceId === sourceId);
      }),
      take(1)
    );
  }

  /**
   * Gets a specific provision object from the in-memory cache.
   * @param provNum The unique provision number.
   * @returns An observable that emits the provision or null if not found.
   */
  public getProvisionObject(provNum: string): Observable<Provision | null> {
    return this.allProvisions$.pipe(
      map(provisions => {
        const foundProvision = provisions.find(p => p.provNum === provNum);
        return foundProvision || null;
      }),
      take(1)
    );
  }

  /**
   * Gets a fully-hydrated Provision object by its provision number.
   * Waits for the cache to be refreshed before returning.
   * @param provNum The provision number to retrieve.
   * @returns An observable of the fully-hydrated Provision or null if not found.
   */
  public getProvisionFullyHydrated(provNum: string): Observable<Provision | null> {
    return this.isCacheRefreshed$.pipe(
      filter(isRefreshed => isRefreshed),
      take(1),
      switchMap(() => this.getProvisionObject(provNum)),
      switchMap(provision => {
        if (!provision) {
          return of(null);
        }
        // Future: Add hydration logic here when connections are ready
        // return this.connectionsService.hydrateEntity(provision, propertyMap);
        return of(provision);
      }),
      catchError(error => {
        console.error('Error fetching Provision object:', error);
        return of(null);
      })
    );
  }

  /**
   * Writes a Provision object to the database.
   * PUT api/{org}/provisions/write with body:
   * { provNum, provData, contractId?, contractNum? }
   * Response: { message: "...", provNum: "..." }
   * @param provision The Provision object to write.
   * @returns An observable of the API response.
   */
  public writeProvision(provision: Provision): Observable<any> {
    // Ensure provNum exists
    if (!provision.provNum) {
      provision.provNum = Date.now().toString();
    }

    // Build the request body with provData wrapper
    // API accepts: { provNum, provData, contractId?, contractNum? }
    const body: {
      provNum: string;
      provData: Provision;
      contractId?: string;
      contractNum?: string;
    } = {
      provNum: provision.provNum,
      provData: provision
    };

    // Include contractId if available (links to contract)
    if (provision.sourceId) {
      body.contractId = provision.sourceId;
    }

    // Include contractNum if available (links to contract)
    if (provision.contractNum) {
      body.contractNum = provision.contractNum;
    }

    const options = {
      headers: { 'Content-Type': 'application/json' }
    };

    return this.http.put<{ message: string; provNum: string }>(
      environment.provisionWriteUrl, body, options
    ).pipe(
      tap(() => {
        this.refreshAllProvisionsRecords().subscribe();
      }),
      catchError(error => {
        console.error('Error writing provision:', error);
        return throwError(() => new Error('Could not write provision to the database.'));
      })
    );
  }

  /**
   * Updates a single attribute of a provision object in the database.
   * @param provNum The unique provision number.
   * @param attribute The attribute name to update.
   * @param value The new value for the attribute.
   * @returns An observable of the API response.
   */
  public updateProvisionAttribute(provNum: string, attribute: string, value: any): Observable<any> {
    const options = {
      headers: { 'Content-Type': 'application/json' }
    };
    const body = {
      provNum,
      attribute,
      value
    };

    // TODO: Add this URL to environment once backend is ready
    // return this.http.put(environment.URL_PROVISION_UPDATE_ATTRIBUTE, body, options)
    return this.http.put(environment.provisionWriteUrl, body, options)
      .pipe(
        tap(() => {
          // console.log('Successfully updated provision attribute.');
          // Refresh cache after updating
          this.refreshAllProvisionsRecords().subscribe();
        }),
        catchError(error => {
          console.error('provisions.service | An error occurred during attribute update:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Archives (soft-deletes) a provision from the database.
   * @param provNum The provision number to archive.
   * @returns An observable of the API response.
   */
  public archiveProvision(provNum: string): Observable<any> {
    const options = {
      headers: { 'Content-Type': 'application/json' }
    };
    const body = {
      provNum,
      action: DatabaseAction.Archive,
      entityType: EntityType.Provision
    };

    // TODO: Update this URL once backend is ready
    return this.http.put(environment.provisionWriteUrl, body, options).pipe(
      tap(() => {
        // console.log('Successfully archived provision.');
        this.refreshAllProvisionsRecords().subscribe();
      }),
      catchError(error => {
        console.error('Error archiving provision:', error);
        return throwError(() => error);
      })
    );
  }

  // Human-readable labels for AI key section names — from the shared CLAUSE_SECTIONS
  // registry (single source of truth) so new clauses get proper provNames, not raw keys.
  private readonly keySectionLabels: { [key: string]: string } = CLAUSE_LABELS_BY_KEYSECTION;

  /**
   * Creates provisions from AI-extracted key sections of a contract.
   * Each found key section becomes a separate provision linked to the source contract.
   *
   * @param keySections The extracted key sections from AI analysis
   * @param contractId The source contract ID
   * @param contractNum The source contract number
   * @param contractFriendlyName The display name of the source contract
   * @param analysisId The AI analysis ID for audit trail
   * @returns Observable that emits as each provision is saved
   */
  public createProvisionsFromAiAnalysis(
    keySections: ExtractedKeySections,
    contractId: string,
    contractNum: string,
    contractFriendlyName: string,
    analysisId: string
  ): Observable<{ created: number; errors: number }> {
    const entries = Object.entries(keySections) as [string, KeySection][];
    const foundSections = entries.filter(([_, section]) => section?.found && section?.language);

    if (foundSections.length === 0) {
      // console.log('[PROVISIONS] No found sections with language to create provisions from');
      return of({ created: 0, errors: 0 });
    }

    // console.log(`[PROVISIONS] Creating ${foundSections.length} provisions from AI analysis`);

    let created = 0;
    let errors = 0;
    let completed = 0;

    return new Observable(subscriber => {
      foundSections.forEach(([sectionKey, section]) => {
        const provision: Provision = {
          provNum: `${Date.now()}-${sectionKey}`,
          provName: this.keySectionLabels[sectionKey] || sectionKey,
          description: section.language,
          sourceType: 'Contract',
          sourceId: contractId,
          contractNum: contractNum,
          contractFriendlyName: contractFriendlyName,
          aiGenerated: true,
          aiAnalysisId: analysisId,
          aiSectionKey: sectionKey,
          approvedProvision: false
        };

        this.writeProvision(provision).subscribe({
          next: () => {
            created++;
            completed++;
            if (completed === foundSections.length) {
              subscriber.next({ created, errors });
              subscriber.complete();
            }
          },
          error: (err) => {
            console.error(`[PROVISIONS] Error creating provision for ${sectionKey}:`, err);
            errors++;
            completed++;
            if (completed === foundSections.length) {
              subscriber.next({ created, errors });
              subscriber.complete();
            }
          }
        });
      });
    });
  }
}
